// LocationSelector.tsx

import React, { useEffect, useState } from "react";
import {
    fetchAdmin1ByCountry,
    fetchAdmin2ByAdmin1,
    fetchCitiesByAdmin2,
    fetchCountries,
    fetchPostalCodesByCity,
} from "../../api/GeoDataAPI";
import { saveOrganizationLocationGeoIds } from "../../api/OrgAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

// ---- Types coming back from your backend ----
// ✅ FIX: make ids "string | number" to tolerate either backend shape
export interface Country {
    id: string | number;
    name: string;
}

export interface Admin1 {
    id: string | number;
    name: string;
}

export interface Admin2 {
    id: string | number;
    name: string;
}

export interface City {
    id: string | number;
    name: string;
}

export interface PostalCode {
    id: string | number;       // ✅ FIX: this is the ID you'll save
    postalCode: string;        // display
    placeName?: string;        // display
}

// ---- Value we bubble up to parent ----
export interface LocationValue {
    country?: Country;
    admin1?: Admin1;
    admin2?: Admin2;
    city?: City;
    postalCode?: PostalCode;
    streetAddress1: string;
    streetAddress2: string;
}

interface LocationSelectorProps {
    token: string;
    onChange?: (value: LocationValue | null) => void;
    className?: string;
    showSaveButton?: boolean;
}

type SaveLocationPayload = {
    countryId: number;
    admin1Id: number;
    admin2Id: number;
    cityId: number;
    postalCode: number; // ✅ FIX: postalCodeId (int)
    streetAddress1: string;
    streetAddress2: string;
};

// ✅ FIX: consistent string conversion for comparing ids
const idEq = (a: string | number | undefined | null, b: string) =>
    a !== undefined && a !== null && String(a) === b;

const GeoLocationSetter: React.FC<LocationSelectorProps> = ({
                                                               token,
                                                               onChange,
                                                               className,
                                                               showSaveButton = true,
                                                           }) => {
    // Data lists
    const [countries, setCountries] = useState<Country[]>([]);
    const [admin1List, setAdmin1List] = useState<Admin1[]>([]);
    const [admin2List, setAdmin2List] = useState<Admin2[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [postalCodes, setPostalCodes] = useState<PostalCode[]>([]);

    // Selected IDs (store as strings always)
    const [countryId, setCountryId] = useState("");
    const [admin1Id, setAdmin1Id] = useState("");
    const [admin2Id, setAdmin2Id] = useState("");
    const [cityId, setCityId] = useState("");
    const [postalCodeId, setPostalCodeId] = useState("");

    // Street fields
    const [streetAddress1, setStreetAddress1] = useState("");
    const [streetAddress2, setStreetAddress2] = useState("");

    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);

    const [loading, setLoading] = useState({
        countries: false,
        admin1: false,
        admin2: false,
        cities: false,
        postalCodes: false,
        saving: false,
    });

    // ---- Helper to notify parent whenever something changes ----
    const emitChange = () => {
        if (!countryId && !streetAddress1 && !streetAddress2) {
            onChange?.(null);
            return;
        }

        // ✅ FIX: use String(id) comparisons
        const country = countries.find((c) => idEq(c.id, countryId));
        const admin1 = admin1List.find((a) => idEq(a.id, admin1Id));
        const admin2 = admin2List.find((a) => idEq(a.id, admin2Id));
        const city = cities.find((c) => idEq(c.id, cityId));
        const postalCode = postalCodes.find((p) => idEq(p.id, postalCodeId));

        onChange?.({
            country,
            admin1,
            admin2,
            city,
            postalCode,
            streetAddress1,
            streetAddress2,
        });
    };

    useEffect(() => {
        emitChange();

    }, [
        countryId,
        admin1Id,
        admin2Id,
        cityId,
        postalCodeId,
        streetAddress1,
        streetAddress2,
    ]);

    // ---- Load Countries on mount ----
    useEffect(() => {
        const loadCountries = async () => {
            try {
                setLoading((prev) => ({ ...prev, countries: true }));
                setLoadError(null);

                const data = await fetchCountries(token);
                setCountries(data);
            } catch (e: any) {
                logApiError(e);
                setLoadError(getUserMessage(e));
            } finally {
                setLoading((prev) => ({ ...prev, countries: false }));
            }
        };

        loadCountries();
    }, [token]);

    // ---- When country changes, reset below + load Admin1 ----
    const handleCountryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCountryId = e.target.value;
        setCountryId(newCountryId);

        setAdmin1Id("");
        setAdmin2Id("");
        setCityId("");
        setPostalCodeId("");
        setAdmin1List([]);
        setAdmin2List([]);
        setCities([]);
        setPostalCodes([]);

        setSaveMessage(null);
        setLoadError(null);

        if (!newCountryId) return;

        try {
            setLoading((prev) => ({ ...prev, admin1: true }));

            const data = await fetchAdmin1ByCountry(token, newCountryId);
            setAdmin1List(data);
        } catch (e: any) {
            logApiError(e);
            setLoadError(getUserMessage(e));
        } finally {
            setLoading((prev) => ({ ...prev, admin1: false }));
        }
    };

    // ---- When admin1 changes, reset below + load Admin2 ----
    const handleAdmin1Change = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newAdmin1Id = e.target.value;
        setAdmin1Id(newAdmin1Id);

        setAdmin2Id("");
        setCityId("");
        setPostalCodeId("");
        setAdmin2List([]);
        setCities([]);
        setPostalCodes([]);

        setSaveMessage(null);
        setLoadError(null);

        if (!newAdmin1Id) return;

        try {
            setLoading((prev) => ({ ...prev, admin2: true }));

            const data = await fetchAdmin2ByAdmin1(token, newAdmin1Id);
            setAdmin2List(data);
        } catch (e: any) {
            logApiError(e);
            setLoadError(getUserMessage(e));
        } finally {
            setLoading((prev) => ({ ...prev, admin2: false }));
        }
    };

    // ---- When admin2 changes, reset below + load Cities ----
    const handleAdmin2Change = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newAdmin2Id = e.target.value;
        setAdmin2Id(newAdmin2Id);

        setCityId("");
        setPostalCodeId("");
        setCities([]);
        setPostalCodes([]);

        setSaveMessage(null);
        setLoadError(null);

        if (!newAdmin2Id) return;

        try {
            setLoading((prev) => ({ ...prev, cities: true }));

            const data = await fetchCitiesByAdmin2(token, newAdmin2Id);
            setCities(data);
        } catch (e: any) {
            logApiError(e);
            setLoadError(getUserMessage(e));
        } finally {
            setLoading((prev) => ({ ...prev, cities: false }));
        }
    };

    // ---- When city changes, reset below + load Postal Codes ----
    const handleCityChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCityId = e.target.value;
        setCityId(newCityId);

        setPostalCodeId("");
        setPostalCodes([]);

        setSaveMessage(null);
        setLoadError(null);

        if (!newCityId) return;

        try {
            setLoading((prev) => ({ ...prev, postalCodes: true }));

            const data = await fetchPostalCodesByCity(token, newCityId);
            setPostalCodes(data);
        } catch (e: any) {
            logApiError(e);
            setLoadError(getUserMessage(e));
        } finally {
            setLoading((prev) => ({ ...prev, postalCodes: false }));
        }
    };

    const handleSave = async () => {
        setSaveError(null);
        setSaveMessage(null);

        const selectedCountry = countries.find((c) => idEq(c.id, countryId));
        const selectedAdmin1 = admin1List.find((a) => idEq(a.id, admin1Id));
        const selectedAdmin2 = admin2List.find((a) => idEq(a.id, admin2Id));
        const selectedCity = cities.find((c) => idEq(c.id, cityId));
        const selectedPostal = postalCodes.find((p) => idEq(p.id, postalCodeId));

        // Name exactly what's missing (labels match the dropdowns).
        const missing: string[] = [];
        if (!selectedCountry) missing.push("Country");
        if (!selectedAdmin1) missing.push("State / Region");
        if (!selectedAdmin2) missing.push("County / District");
        if (!selectedCity) missing.push("City");
        if (!selectedPostal) missing.push("Postal Code");
        if (!streetAddress1.trim()) missing.push("Street Address 1");
        if (missing.length > 0) {
            setSaveError(`Still missing: ${missing.join(", ")}. Everything above Street Address 2 is required.`);
            return;
        }
        if (!selectedCountry || !selectedAdmin1 || !selectedAdmin2 || !selectedCity || !selectedPostal) {
            return; // unreachable — narrows the types for the payload below
        }

        const payload: SaveLocationPayload = {
            countryId: parseInt(String(selectedCountry.id), 10),
            admin1Id: parseInt(String(selectedAdmin1.id), 10),
            admin2Id: parseInt(String(selectedAdmin2.id), 10),
            cityId: parseInt(String(selectedCity.id), 10),
            postalCode: parseInt(String(selectedPostal.id), 10),
            streetAddress1: streetAddress1.trim(),
            streetAddress2: streetAddress2.trim(),
        };

        if (
            Number.isNaN(payload.countryId) ||
            Number.isNaN(payload.admin1Id) ||
            Number.isNaN(payload.admin2Id) ||
            Number.isNaN(payload.cityId) ||
            Number.isNaN(payload.postalCode)
        ) {
            setSaveError("One of the selected IDs is not numeric. Backend expects int IDs.");
            return;
        }

        try {
            setLoading((prev) => ({ ...prev, saving: true }));

            await saveOrganizationLocationGeoIds(token, payload);

            setSaveMessage("Saved.");
        } catch (e: any) {
            logApiError(e);
            setSaveError(getUserMessage(e));
        } finally {
            setLoading((prev) => ({ ...prev, saving: false }));
        }
    };

    return (
        <div className={className}>
            <h3 style={{ marginBottom: "0.5rem" }}>Location</h3>

            {loadError && (
                <div className="error-message" style={{ marginBottom: "0.5rem" }}>
                    {loadError}
                </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {/* Country */}
                <label>
                    <div>Country</div>
                    <select value={countryId} onChange={handleCountryChange} disabled={loading.countries}>
                        <option value="">{loading.countries ? "Loading..." : "Select a country"}</option>
                        {countries.map((c) => (
                            <option key={String(c.id)} value={String(c.id)}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </label>

                {/* Admin1 */}
                <label>
                    <div>State / Region (Admin1)</div>
                    <select value={admin1Id} onChange={handleAdmin1Change} disabled={!countryId || loading.admin1}>
                        <option value="">
                            {!countryId ? "Select a country first" : loading.admin1 ? "Loading..." : "Select a state/region"}
                        </option>
                        {admin1List.map((a) => (
                            <option key={String(a.id)} value={String(a.id)}>
                                {a.name}
                            </option>
                        ))}
                    </select>
                </label>

                {/* Admin2 */}
                <label>
                    <div>County / District (Admin2)</div>
                    <select value={admin2Id} onChange={handleAdmin2Change} disabled={!admin1Id || loading.admin2}>
                        <option value="">
                            {!admin1Id ? "Select Admin1 first" : loading.admin2 ? "Loading..." : "Select admin2"}
                        </option>
                        {admin2List.map((a) => (
                            <option key={String(a.id)} value={String(a.id)}>
                                {a.name}
                            </option>
                        ))}
                    </select>
                </label>

                {/* City */}
                <label>
                    <div>City</div>
                    <select value={cityId} onChange={handleCityChange} disabled={!admin2Id || loading.cities}>
                        <option value="">
                            {!admin2Id ? "Select Admin2 first" : loading.cities ? "Loading..." : "Select a city"}
                        </option>
                        {cities.map((c) => (
                            <option key={String(c.id)} value={String(c.id)}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </label>

                {/* Postal Code */}
                <label>
                    <div>Postal Code</div>
                    <select value={postalCodeId} onChange={(e) => setPostalCodeId(e.target.value)} disabled={!cityId || loading.postalCodes}>
                        <option value="">
                            {!cityId ? "Select a city first" : loading.postalCodes ? "Loading..." : "Select postal code"}
                        </option>
                        {postalCodes.map((p) => (
                            <option key={String(p.id)} value={String(p.id)}>
                                {p.postalCode} {p.placeName ? `(${p.placeName})` : ""}
                            </option>
                        ))}
                    </select>
                </label>

                {/* Street Address 1 */}
                <label>
                    <div>Street Address 1</div>
                    <input
                        type="text"
                        value={streetAddress1}
                        onChange={(e) => setStreetAddress1(e.target.value)}
                        placeholder="123 Main St"
                    />
                </label>

                {/* Street Address 2 */}
                <label>
                    <div>Street Address 2</div>
                    <input
                        type="text"
                        value={streetAddress2}
                        onChange={(e) => setStreetAddress2(e.target.value)}
                        placeholder="Apt / Unit / Suite (optional)"
                    />
                </label>

                {showSaveButton && (
                    <>
                        <button type="button" onClick={handleSave} disabled={loading.saving}>
                            {loading.saving ? "Saving..." : "Save Location"}
                        </button>
                        {saveError && (
                            <div className="error-message" style={{ marginTop: "0.25rem" }}>{saveError}</div>
                        )}
                        {saveMessage && (
                            <div className="success-message" style={{ marginTop: "0.25rem" }}>{saveMessage}</div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default GeoLocationSetter;